import React, { useMemo, useState, useRef } from 'react';
import { parseThemeColor, getThemeColorsMap } from '../utils/theme-helper.js';
import { Icons } from './Icon.jsx';

const ThemeColorBrowser = ({ value, onChange, onClose }) => {
    const [search, setSearch] = useState('');
    // Capture initial value on mount for Cancel functionality
    const initialValue = useRef(value).current;

    const colors = useMemo(() => {
        const colorsMap = getThemeColorsMap();
        return Object.entries(colorsMap).map(([key, rgbValue]) => ({
            key,
            value: rgbValue,
            cssValue: `rgb(${rgbValue.replace(/ /g, ', ')})`
        }));
    }, []);

    const filteredColors = colors.filter(c => 
        c.key.toLowerCase().includes(search.toLowerCase()) || 
        c.value.toLowerCase().includes(search.toLowerCase())
    );

    const handleCancel = () => {
        if (initialValue !== value) {
            onChange(initialValue);
        }
        onClose();
    };

    const handleSelect = () => {
        // Value is already updated via preview, just close
        onClose();
    };

    return (
        <div className="w-64 bg-white rounded-lg shadow-xl border border-gray-200 flex flex-col max-h-96">
            <div className="p-3 border-b border-gray-200 bg-slate-50 rounded-t-lg">
                <div className="flex justify-between items-center mb-2">
                    <h3 className="text-xs font-bold text-gray-700 uppercase tracking-wider">Theme Colors</h3>
                    <button onClick={handleCancel} className="text-gray-400 hover:text-gray-600">
                        <Icons.Close className="w-3 h-3" />
                    </button>
                </div>
                {/* Search Input */}
                <div className="relative">
                    <Icons.Search className="w-3 h-3 absolute left-2 top-1/2 -translate-y-1/2 text-gray-400" />
                    <input 
                        className="w-full pl-7 pr-2 py-1 text-xs border border-gray-300 rounded focus:border-blue-500 outline-none"
                        placeholder="Search colors..."
                        value={search}
                        onChange={e => setSearch(e.target.value)}
                        autoFocus
                    />
                </div>
            </div>
            
            <div className="flex-1 overflow-y-auto p-2 scrollbar-thin scrollbar-thumb-slate-300">
                <div className="grid grid-cols-1 gap-1">
                    {filteredColors.length === 0 && <div className="text-xs text-gray-400 text-center py-4">No colors found</div>}
                    {filteredColors.map(color => {
                        const isSelected = value === color.key;
                        return (
                            <button
                                key={color.key}
                                onClick={() => onChange(color.key)} // Preview on click
                                className={`flex items-center gap-3 p-2 rounded text-left transition-colors w-full ${isSelected ? 'bg-blue-50 ring-1 ring-blue-500' : 'hover:bg-slate-50'}`}
                            >
                                <div 
                                    className="w-8 h-8 rounded border border-gray-200 shadow-sm shrink-0"
                                    style={{ backgroundColor: color.cssValue }}
                                />
                                <div className="flex flex-col min-w-0">
                                    <span className="text-xs font-medium text-gray-900 truncate">{color.key}</span>
                                    <span className="text-[10px] text-gray-400 truncate font-mono">{color.value}</span>
                                </div>
                                {isSelected && <Icons.Check className="w-3 h-3 text-blue-500 ml-auto" />}
                            </button>
                        );
                    })}
                </div>
            </div>
            
            <div className="p-2 border-t border-gray-200 bg-slate-50 rounded-b-lg flex gap-2">
                <button 
                    onClick={handleCancel}
                    className="flex-1 py-1.5 text-xs font-medium text-gray-600 hover:bg-white hover:text-gray-800 border border-transparent hover:border-gray-300 rounded transition-all"
                >
                    Cancel
                </button>
                <button 
                    onClick={handleSelect}
                    className="flex-1 py-1.5 text-xs font-medium bg-blue-600 text-white hover:bg-blue-700 rounded shadow-sm transition-all"
                >
                    Select
                </button>
            </div>
        </div>
    );
};

export default ThemeColorBrowser;